/**
 * useFunnelExperience Hook
 *
 * Evaluates the funnel step experience for the current page context.
 *
 * Behavior:
 * - Experience shows every time the component mounts (page visit / reload).
 * - "dismiss" (X button) hides it for this render only — comes back on next visit.
 * - "decline" ("No thanks" text) suppresses it for the entire browser session.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useFunnelRuntimeContext } from './use-funnel-runtime-context'
import type { StepExperience, FunnelOffer, PageContext } from './funnel-runtime.types'

const DECLINED_KEY = 'funnel_declined_steps'

interface UseFunnelExperienceResult {
  loading: boolean
  experience: StepExperience | null
  offer: FunnelOffer | null
  experienceType: string | null
  dismiss: () => void
  decline: () => void
}

function getDeclinedSteps(): Set<string> {
  try {
    const raw = sessionStorage.getItem(DECLINED_KEY)
    if (raw) return new Set(JSON.parse(raw) as string[])
  } catch { /* ignore */ }
  return new Set()
}

function addDeclinedStep(stepId: string): void {
  try {
    const declined = getDeclinedSteps()
    declined.add(stepId)
    sessionStorage.setItem(DECLINED_KEY, JSON.stringify([...declined]))
  } catch { /* ignore */ }
}

export function useFunnelExperience(
  pageType: PageContext['pageType'],
  context: Omit<PageContext, 'pageType'>,
): UseFunnelExperienceResult {
  const { loading: providerLoading, evaluatePageStep, assignedFunnelId } = useFunnelRuntimeContext()
  const [loading, setLoading] = useState(true)
  const [experience, setExperience] = useState<StepExperience | null>(null)
  const [hidden, setHidden] = useState(false)
  const stepIdRef = useRef<string | null>(null)

  useEffect(() => {
    setHidden(false)
    setExperience(null)
    setLoading(true)

    if (providerLoading) return

    if (!assignedFunnelId) {
      setLoading(false)
      return
    }

    let cancelled = false

    async function evaluate() {
      try {
        const result = await evaluatePageStep({
          pageType,
          url: context.url,
          productId: context.productId,
          collectionId: context.collectionId,
        })

        if (cancelled) return

        if (result.hasExperience && result.step) {
          if (getDeclinedSteps().has(result.step.id)) {
            setHidden(true)
          } else {
            stepIdRef.current = result.step.id
            setExperience(result.step)
          }
        }
      } catch (err) {
        console.error('[useFunnelExperience] evaluate failed:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    evaluate()
    return () => { cancelled = true }
  }, [providerLoading, assignedFunnelId, pageType, context.url, context.productId, context.collectionId, evaluatePageStep])

  const dismiss = useCallback(() => {
    setHidden(true)
  }, [])

  const decline = useCallback(() => {
    if (stepIdRef.current) {
      addDeclinedStep(stepIdRef.current)
    }
    setHidden(true)
  }, [])

  const isVisible = !loading && !hidden && experience !== null

  return {
    loading: loading || providerLoading,
    experience: isVisible ? experience : null,
    offer: isVisible ? (experience?.offer ?? null) : null,
    experienceType: isVisible ? (experience?.experienceType ?? null) : null,
    dismiss,
    decline,
  }
}
